import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, ChevronDown, Filter } from "lucide-react";

export function useFlaggerFilter() {
  const [flaggers, setFlaggers] = useState([]);
  const [selectedFlaggerIds, setSelectedFlaggerIds] = useState([]);
  const seenRef = useRef(null);
  const flaggedByParam = useMemo(() => {
    if (flaggers.length === 0) return "";
    if (selectedFlaggerIds.length === 0) return null;
    if (flaggers.every((f) => selectedFlaggerIds.includes(f.id))) return "";
    return selectedFlaggerIds.join(",");
  }, [selectedFlaggerIds, flaggers]);
  const syncFlaggers = useCallback((nextFlaggers) => {
    if (!Array.isArray(nextFlaggers)) return;
    setFlaggers(nextFlaggers);
    const allIds = nextFlaggers.map((f) => f.id);
    if (seenRef.current === null) {
      seenRef.current = new Set(allIds);
      setSelectedFlaggerIds(allIds);
      return;
    }
    const newIds = allIds.filter((id) => !seenRef.current.has(id));
    allIds.forEach((id) => seenRef.current.add(id));
    if (newIds.length > 0) setSelectedFlaggerIds((prev) => [...prev, ...newIds]);
  }, []);
  return { flaggers, selectedFlaggerIds, setSelectedFlaggerIds, flaggedByParam, syncFlaggers };
}

export function FlaggerMultiSelect({ flaggers = [], selected = [], onChange, title = "Filter by who merged the duplicate" }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => containerRef.current && !containerRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);
  const allIds = useMemo(() => flaggers.map((f) => f.id), [flaggers]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));
  const selectedFlagger = flaggers.find((f) => f.id === selected[0]);
  const toggleOne = (id) => onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  const buttonLabel =
    allSelected
      ? "All flaggers"
      : selected.length === 0
        ? "No flaggers selected"
        : selected.length === 1
          ? selectedFlagger?.label || "1 selected"
          : `${selected.length} flaggers selected`;
  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-2 rounded-md border border-input bg-background px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        title={title}
      >
        <Filter className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="max-w-[14rem] truncate">{buttonLabel}</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-64 max-w-[calc(100vw-2rem)] rounded-md border border-border bg-background shadow-lg">
          <button type="button" onClick={() => !allSelected && onChange(allIds)} disabled={allSelected} className="flex w-full items-center justify-between border-b border-border px-3 py-2 text-sm font-medium hover:bg-muted/40 disabled:cursor-default disabled:text-muted-foreground disabled:hover:bg-transparent">
            <span>Select all</span>
            {allSelected && <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
          </button>
          <div className="max-h-64 overflow-y-auto py-1">
            {flaggers.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">No flaggers yet</p>}
            {flaggers.map((f) => (
              <label key={f.id} className="flex cursor-pointer items-center gap-2 px-3 py-2.5 text-sm hover:bg-muted/40 sm:py-1.5">
                <input type="checkbox" checked={selected.includes(f.id)} onChange={() => toggleOne(f.id)} className="h-4 w-4 accent-primary" />
                <span className="truncate">{f.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
